/**
 * Search response normalization
 *
 * Transforms the SoloLand /search response into the Alpitour shape
 * consumed by results.vue and pad.vue.
 *
 * ── SoloLand /search response ────────────────────────────────────────────
 *   Envelope: { data: Hotel[], metadata: { count, request_id, eventId } }
 *
 *   Hotel:
 *     { ext_id: "AL50731", brand: "AL", hotel_id: "50731",
 *       name: "LITORANEO SUITE HOTEL",
 *       rating: { stars: 4 },
 *       location: { country: "", city: "Rimini", coordinates: { latitude, longitude } },
 *       description: "...",
 *       thumbnail_url: "https://...",
 *       room: { name: "STILO ROOM",
 *               rate: { solution: { id: "...", room_ref: 1 },
 *                       description: "MEZZA PENSIONE",
 *                       pricing: { total: 615.6, net: 554.04, base: 615.6, extra: 0 } } } }
 *
 * ── Alpitour shape (consumed by results.vue / pad.vue) ───────────────────
 *   Envelope: { data: { products: Product[], statics: { [pid]: Static }, keyds: {}, occupancy: {} } }
 *
 *   Product:
 *     { keyd, pid, brand, rating, room, accommodation, price,
 *       departureAirportCode, departureAirportDescription,
 *       departureDate, returnDate, nights, days, pidkey, solution_id }
 *
 *   Static (keyed by pid):
 *     { urlThumbProdotto, nomeProdotto, city, abstractProdotto }
 */

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

/**
 * Calculate the number of nights between two YYYY-MM-DD date strings.
 */
const calc_nights = (checkin, checkout) => {
  if (!checkin || !checkout) return 1;
  const d1 = new Date(checkin);
  const d2 = new Date(checkout);
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 1;
};

// ---------------------------------------------------------------------------
//  Single-item transforms
// ---------------------------------------------------------------------------

/**
 * Normalize a SoloLand hotel into the Alpitour product shape.
 *
 * pid format: "hotelId-brand" (e.g. "50731-AL") so that
 * get_brand(item.pid.split('-')[1]) resolves the brand correctly in pad.vue.
 */
const normalize_product = (hotel, { checkin, checkout, nights }) => {
  const rating = hotel.rating && hotel.rating.stars;
  const room = hotel.room || {};
  const rate = room.rate || {};
  const pricing = rate.pricing || {};
  const solution = rate.solution || {};
  const roomName = room.name || "";

  return {
    keyd: hotel.ext_id || "",
    pid: `${hotel.hotel_id || ""}-${hotel.brand || ""}`,
    brand: hotel.brand || "",
    rating: rating != null ? String(rating) : "",
    room: roomName,
    accommodation: rate.description || "",
    price: pricing.total != null ? String(pricing.total) : "0",
    departureAirportCode: "",
    departureAirportDescription: "",
    departureDate: checkin,
    returnDate: checkout,
    nights,
    days: nights,
    pidkey: `${hotel.ext_id || ""}_${roomName.split(" ").join("_")}`,
    solution_id: solution.id || "",
    room_ref: solution.roomRef != null ? solution.roomRef : (solution.room_ref != null ? solution.room_ref : ""),
  };
};

/**
 * Build the statics map from SoloLand hotels.
 * Keyed by pid ("hotelId-brand") to match statics[product.pid] lookups.
 */
const build_statics = (hotels) => {
  const statics = {};
  for (const hotel of hotels) {
    const pid = `${hotel.hotel_id || ""}-${hotel.brand || ""}`;
    if (!statics[pid]) {
      const location = hotel.location || {};
      statics[pid] = {
        urlThumbProdotto: hotel.thumbnail_url || "",
        nomeProdotto: hotel.name || "",
        city: location.city || "",
        abstractProdotto: hotel.description || "",
      };
    }
  }
  return statics;
};

// ---------------------------------------------------------------------------
//  Public API
// ---------------------------------------------------------------------------

/**
 * Detect whether a search response is SoloLand format and normalize it
 * to the Alpitour shape consumed by results.vue.
 *
 * Detection:
 *   - SoloLand: responseData.data is an Array of hotels
 *   - Alpitour: responseData.data is an Object with { products, statics, keyds }
 *
 * @param {Object} responseData - response.data (Axios body)
 * @param {{ checkin: string, checkout: string }} dates - checkin/checkout dates
 * @returns {Object} Alpitour-shaped { data: { products, statics, keyds, occupancy } }
 */
export const normalize_search_response = (responseData, dates) => {
  if (!responseData) return responseData;

  // Already in Alpitour format — data.products exists
  if (
    responseData.data &&
    !Array.isArray(responseData.data) &&
    responseData.data.products
  ) {
    return responseData;
  }

  // SoloLand format — data is an array of hotels
  const hotels = Array.isArray(responseData.data) ? responseData.data : [];

  const checkin = (dates && dates.checkin) || "";
  const checkout = (dates && dates.checkout) || "";
  const nights = calc_nights(checkin, checkout);

  const context = { checkin, checkout, nights };

  return {
    data: {
      products: hotels.map((hotel) => normalize_product(hotel, context)),
      statics: build_statics(hotels),
      keyds: {},
      occupancy: {},
    },
  };
};
